/* eslint-disable @typescript-eslint/no-non-null-assertion */
// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    Input,
    InputOnChangeData,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuOpenChangeData,
    MenuPopover,
    MenuTrigger,
    Spinner,
    Subtitle2Stronger,
    makeStaticStyles,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import React from 'react';
import { Drawer, DrawerBody, DrawerHeader, DrawerHeaderTitle } from '@fluentui/react-drawer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useChat, useFile } from '../../../libs/hooks';
import { AlertType } from '../../../libs/models/AlertType';
import { Bot } from '../../../libs/models/Bot';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { FeatureKeys, IConversationDisplayMode } from '../../../redux/features/app/AppState';
import { addAlert, setConversationDisplayMode } from '../../../redux/features/app/appSlice';
import { Conversations } from '../../../redux/features/conversations/ConversationsState';
import { Breakpoints, customTokens } from '../../../styles';
import { FileUploader } from '../../FileUploader';
import { CommentMultiple20, Dismiss20, Filter20 } from '../../shared/BundledIcons';
import { isToday } from '../../utils/TextUtils';
import { ChatListSection } from './ChatListSection';
import { NewBotMenu } from './bot-menu/NewBotMenu';
import { SimplifiedNewBotMenu } from './bot-menu/SimplifiedNewBotMenu';
import { PanelLeftContract28Regular, DismissCircle32Regular, PanelLeftExpand28Regular } from '@fluentui/react-icons';
import useScreenSize from '../../../libs/hooks/useScreenSize';
import CacheUtils from '../../../libs/utils/CacheUtils';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexShrink: 0,
        width: '320px',
        backgroundColor: tokens.colorNeutralBackground4,
        flexDirection: 'column',
        ...shorthands.overflow('hidden'),
        ...Breakpoints.small({
            width: '64px',
        }),
    },
    headerClosed: {
        width: '55px',
    },
    list: {
        overflowY: 'auto',
        overflowX: 'hidden',
        '&:hover': {
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: tokens.colorScrollbarOverlay,
                visibility: 'visible',
            },
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: tokens.colorSubtleBackground,
        },
        width: '100%',
    },
    navigationDesktopBars: {
        display: 'flex',
        justifyContent: 'center',
        alignSelf: 'flex-start',
        paddingTop: '3px',
        paddingRight: '5px',
        paddingLeft: '12px',
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    navigationBars: {
        display: 'none',
        justifyContent: 'center',
        ...Breakpoints.small({
            display: 'flex',
            paddingRight: '25px',
        }),
    },
    navigationIcon: {
        cursor: 'pointer',
    },
    createNewChatIcon: {
        cursor: 'pointer',
        paddingTop: '15px',
    },
    closedBars: {
        paddingTop: '16px',
        flexDirection: 'column',
        alignItems: 'center',
    },
    drawerBody: {
        backgroundImage: 'none',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        paddingRight: '0',
        paddingLeft: '7px',
        display: 'flex',
    },
    drawerHeaderTitle: {
        paddingRight: '0',
        '> .fui-DrawerHeaderTitle__action': {
            paddingTop: '5px',
        },
        '> .fui-DrawerHeaderTitle__heading': {
            width: '100%',
        },
    },
    drawerCloseButton: {
        height: '32px',
        width: '32px',
        '> .fui-Button__icon': {
            height: '32px',
            width: '32px',
        },
    },
    chatListDrawer: {
        backgroundColor: '#f0f0f0',
        display: 'none',
        ...Breakpoints.small({
            display: 'flex',
        }),
        '> .fui-DrawerHeader': {
            paddingTop: '5px',
            paddingRight: '20px',
        },
    },
    navigationDrawer: {
        display: 'block',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginRight: tokens.spacingVerticalM,
        marginLeft: tokens.spacingHorizontalXL,
        marginTop: '10px',
        alignItems: 'center',
        height: '85px',
        minHeight: '85px',
        ...Breakpoints.small({
            marginLeft: '4px',
            flexDirection: 'row-reverse',
            justifyContent: 'flex-end',
            height: '35px',
            minHeight: '35px',
            marginTop: '3px',
        }),
    },

    headerActions: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        ...Breakpoints.small({
            flexDirection: 'row-reverse',
        }),
    },
    title: {
        flexGrow: 1,
        ...Breakpoints.small({
            display: 'none',
        }),
        fontSize: tokens.fontSizeBase500,
    },
    input: {
        flexGrow: 1,
        ...shorthands.padding(tokens.spacingHorizontalNone),
        ...shorthands.border(tokens.borderRadiusNone),
        backgroundColor: tokens.colorSubtleBackground,
        fontSize: tokens.fontSizeBase500,
    },
    noConversationLabel: {
        marginTop: 0,
        paddingBottom: tokens.spacingVerticalXS,
        marginLeft: tokens.spacingHorizontalXL,
        marginRight: tokens.spacingHorizontalXL,
        fontWeight: tokens.fontWeightRegular,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    conversationModeMenu: {
        width: '100%',
        marginTop: '8px',
        marginBottom: '10px',
        ...Breakpoints.small({
            display: 'flex',
            flexDirection: 'column',
            marginLeft: '0',
            alignItems: 'center',
            width: 'auto',
            marginTop: '0',
            marginBottom: '0',
        }),
    },
    menuButton: {
        ':hover': {
            color: 'rgb(6, 116, 126)',
        },
        ':active': {
            color: 'rgb(6, 116, 126) !important',
        },
    },
    hideOnSmall: {
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    showOnSmall: {
        display: 'none',
        ...Breakpoints.small({
            display: 'flex',
        }),
    },
});

const useStaticStyles = makeStaticStyles({
    '.chatList': {
        display: 'flex',
    },
});

interface ConversationsView {
    filteredConversations?: Conversations;
    latestConversations?: Conversations;
    olderConversations?: Conversations;
}

enum AppState {
    ReloadingConversations,
    Chat,
}

interface IChatListProps {
    className?: string;
}

export const ChatList: React.FC<IChatListProps> = (props) => {
    const classes = useClasses();

    useStaticStyles();

    const screenSize = useScreenSize();

    const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);
    const { conversations, isLoadingMessages } = useAppSelector((state: RootState) => state.conversations);

    const [isFiltering, setIsFiltering] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [conversationsView, setConversationsView] = useState<ConversationsView>({
        latestConversations: conversations,
    });
    const [isOpen, setIsOpen] = React.useState(false);
    const [isLeftPanelOpen, setIsLeftPanelOpen] = React.useState(
        CacheUtils.getStoredDataByKey(`setIsLeftPanelOpen_${activeUserInfo?.id}`, true) === 'true' || false,
    );
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [scrollToFetch, setScrollToFetch] = useState(false);

    const [selectedConversationOption, setSelectedConversationOption] = useState('Active conversations');

    const chat = useChat();
    const fileHandler = useFile();
    const dispatch = useAppDispatch();
    const [appState, setAppState] = useState(AppState.Chat);
    const { conversationDisplayMode } = useAppSelector((state: RootState) => state.app);

    const sortConversations = (conversations: Conversations): ConversationsView => {
        // sort conversations by last activity
        const sortedIds = Object.keys(conversations).sort((a, b) => {
            if (conversations[a].lastUpdatedTimestamp === undefined) {
                return 1;
            }
            if (conversations[b].lastUpdatedTimestamp === undefined) {
                return -1;
            }
            // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
            return (
                new Date(conversations[b].lastUpdatedTimestamp!).getTime() -
                new Date(conversations[a].lastUpdatedTimestamp!).getTime()
            );
        });

        // Add conversations to sortedConversations in the order of sortedIds.
        const latestConversations: Conversations = {};
        const olderConversations: Conversations = {};
        sortedIds.forEach((id) => {
            if (isToday(new Date(conversations[id].lastUpdatedTimestamp ?? 0))) {
                latestConversations[id] = conversations[id];
            } else {
                olderConversations[id] = conversations[id];
            }
        });
        return {
            latestConversations: latestConversations,
            olderConversations: olderConversations,
        };
    };

    const onChangeHiddenChatsOption = (_selectedConversationOption: MenuOpenChangeData) => {
        if (_selectedConversationOption.type !== 'menuItemClick') {
            return;
        }

        const _conversationOption = (_selectedConversationOption.event.target as HTMLElement).innerText;

        setPage(1);
        setHasMore(true);
        setSelectedConversationOption(_conversationOption);
        setAppState(AppState.ReloadingConversations);

        switch (_conversationOption) {
            case 'Active conversations':
                dispatch(setConversationDisplayMode(IConversationDisplayMode.Active));
                break;
            case 'Hidden conversations':
                dispatch(setConversationDisplayMode(IConversationDisplayMode.Hidden));
                break;
            case 'All conversations':
                dispatch(setConversationDisplayMode(IConversationDisplayMode.All));
                break;
        }

        /* TODO: Pass empty [] for conversations or check if all conversation are already loaded */
        if (_conversationOption === 'Active conversations' || _conversationOption === 'All conversations') {
            void Promise.all([
                chat.loadChats(conversations, _conversationOption === 'All conversations').then((result) => {
                    if (result.success) {
                        setAppState(AppState.Chat);
                    }
                }),
            ]);
        } else {
            void Promise.all([
                chat
                    .loadHiddenChats(conversations)
                    .then((result) => {
                        if (result.success) {
                            setAppState(AppState.Chat);
                        } else {
                            console.error('Failed to load hidden chats');
                            setAppState(AppState.Chat);
                        }
                    })
                    .catch((error) => {
                        console.error('Failed to load hidden chats', error);
                        setAppState(AppState.Chat);
                    }),
            ]);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setIsOpen(false);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        // Ensure local component state is in line with app state.
        if (filterText !== '') {
            // Reapply search string to the updated conversations list.
            const filteredConversations: Conversations = {};
            for (const key in conversations) {
                if (conversations[key].title.toLowerCase().includes(filterText.toLowerCase())) {
                    filteredConversations[key] = conversations[key];
                }
            }
            setConversationsView({ filteredConversations: filteredConversations });
        } else {
            // If no search string, show full conversations list.

            const allExistingConversations = {
                ...(conversationsView.filteredConversations ?? {}),
                ...(conversationsView.latestConversations ?? {}),
                ...(conversationsView.olderConversations ?? {}),
                ...conversations,
            };

            setConversationsView(sortConversations(allExistingConversations));
            setScrollToFetch(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversations, filterText]);

    const getNextConversations = async (page: number) => {
        let result;

        if (conversationDisplayMode === IConversationDisplayMode.Active ||
            conversationDisplayMode === IConversationDisplayMode.All) {
            result = await chat.loadChats(conversations, selectedConversationOption == 'All conversations', page, undefined, true);
        } else {
            result = await chat.loadHiddenChats(conversations, page, undefined, true);
        }

        if (result.success) {
            setAppState(AppState.Chat);
        }

        if (result.count === 0 || result.count < 20) {
            setHasMore(false);
            setScrollToFetch(false);
        }
    };

    useEffect(() => {
        if (page > 1) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            getNextConversations(page);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        if (scrollToFetch) {
            setPage((prevPage) => prevPage + 1);
        }
    }, [scrollToFetch]);

    const fetchData = () => {
        if (hasMore) {
            setScrollToFetch(true);
        }
    };

    const onFilterClick = () => {
        setIsFiltering(true);
    };

    const onFilterCancel = () => {
        setFilterText('');
        setIsFiltering(false);
    };

    const onSearch = (ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        ev.preventDefault();
        setFilterText(data.value);
    };

    const fileUploaderRef = useRef<HTMLInputElement>(null);

    const onUpload = useCallback(
        (file: File) => {
            fileHandler.loadFile<Bot>(file, chat.uploadBot).catch((error) =>
                dispatch(
                    addAlert({
                        message: `Failed to parse uploaded file. ${error instanceof Error ? error.message : ''}`,
                        type: AlertType.Error,
                    }),
                ),
            );
        },
        [fileHandler, chat, dispatch],
    );

    const header = (
        <div className={classes.header}>
            <div className={classes.headerActions}>
                {features[FeatureKeys.SimplifiedExperience].enabled ? (
                    <SimplifiedNewBotMenu
                        onFileUpload={() => {
                            fileUploaderRef.current?.click();
                        }}
                        onCreateNewChat={() => {
                            setIsOpen(false);
                        }}
                        title="New chat"
                    />
                ) : (
                    <>
                        {!isFiltering && (
                            <>
                                <Subtitle2Stronger className={classes.title}>Conversations</Subtitle2Stronger>
                                <Button icon={<Filter20 />} appearance="transparent" onClick={onFilterClick} />
                                <NewBotMenu onFileUpload={() => fileUploaderRef.current?.click()} />
                                <FileUploader
                                    ref={fileUploaderRef}
                                    acceptedExtensions={['.json']}
                                    onSelectedFile={onUpload}
                                />
                            </>
                        )}
                        {isFiltering && (
                            <>
                                <Input
                                    placeholder="Filter by name"
                                    className={mergeClasses(classes.input, classes.title)}
                                    onChange={onSearch}
                                    autoFocus
                                />
                                <Button icon={<Dismiss20 />} appearance="transparent" onClick={onFilterCancel} />
                            </>
                        )}
                    </>
                )}
                {features[FeatureKeys.HiddenChats].enabled && (
                    <div className={classes.conversationModeMenu}>
                        <Menu
                            onOpenChange={(_e, data) => {
                                onChangeHiddenChatsOption(data);
                            }}
                        >
                            <MenuTrigger disableButtonEnhancement>
                                <MenuButton
                                    appearance="outline"
                                    icon={<CommentMultiple20 color={customTokens.ekfbTeal} />}
                                    style={{ border: '1px solid rgb(189, 189, 189)', width: '100%' }}
                                    className={classes.menuButton}
                                    disabled={appState === AppState.ReloadingConversations || isLoadingMessages}
                                >
                                    <span className={classes.hideOnSmall}>{selectedConversationOption}</span>
                                </MenuButton>
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    <MenuItem>Active conversations</MenuItem>
                                    <MenuItem>Hidden conversations</MenuItem>
                                    <MenuItem>All conversations</MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </div>
                )}
            </div>
            <div className={classes.navigationDesktopBars}>
                <PanelLeftContract28Regular
                    className={classes.navigationIcon}
                    onClick={() => {
                        setIsLeftPanelOpen(false);
                        CacheUtils.setStoredDataByKey(
                            `setIsLeftPanelOpen_${activeUserInfo?.id}`,
                            'false',
                            undefined,
                            true,
                        );
                    }}
                />
            </div>
        </div>
    );

    const body = (
        <div id="chat-list" aria-label={'chat list'} className={classes.list}>
            {appState !== AppState.ReloadingConversations ? (
                <>
                    {Object.keys(conversations).length > 0 ? (
                        <>
                            {isFiltering && filterText.length > 0 ? (
                                <>
                                    {conversationsView.filteredConversations && (
                                        <ChatListSection
                                            onSelectChat={() => {
                                                setIsOpen(false);
                                            }}
                                            conversations={conversationsView.filteredConversations}
                                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                            fetchData={fetchData}
                                            hasMore={hasMore}
                                        />
                                    )}
                                </>
                            ) : (
                                <>
                                    {conversationsView.latestConversations && (
                                        <ChatListSection
                                            onSelectChat={() => {
                                                setIsOpen(false);
                                            }}
                                            header="Today"
                                            conversations={conversationsView.latestConversations}
                                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                            fetchData={fetchData}
                                            hasMore={hasMore}
                                        />
                                    )}
                                    {conversationsView.olderConversations && (
                                        <ChatListSection
                                            onSelectChat={() => {
                                                setIsOpen(false);
                                            }}
                                            header="Older"
                                            conversations={conversationsView.olderConversations}
                                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                            fetchData={fetchData}
                                            hasMore={hasMore}
                                        />
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <p className={classes.noConversationLabel}>No conversations</p>
                    )}
                </>
            ) : (
                <>
                    <Spinner
                        label="Loading conversations..."
                        className={classes.hideOnSmall}
                        labelPosition="above"
                        style={{ marginTop: '50%' }}
                    />
                    <Spinner className={classes.showOnSmall} labelPosition="above" style={{ marginTop: '50%' }} />
                </>
            )}
        </div>
    );

    const contentDesktop = screenSize.width > 744 && (
        <div className={isLeftPanelOpen ? classes.root : mergeClasses(classes.root, classes.headerClosed)}>
            {isLeftPanelOpen ? (
                header
            ) : (
                <div className={mergeClasses(classes.navigationDesktopBars, classes.closedBars)}>
                    <PanelLeftExpand28Regular
                        className={classes.navigationIcon}
                        onClick={() => {
                            setIsLeftPanelOpen(true);
                            CacheUtils.setStoredDataByKey(
                                `setIsLeftPanelOpen_${activeUserInfo?.id}`,
                                'true',
                                undefined,
                                true,
                            );
                        }}
                    />
                    {features[FeatureKeys.SimplifiedExperience].enabled && (
                        <div className={classes.createNewChatIcon}>
                            <SimplifiedNewBotMenu
                                isSmallButton={true}
                                onFileUpload={() => {
                                    fileUploaderRef.current?.click();
                                }}
                                onCreateNewChat={() => {
                                    setIsOpen(false);
                                }}
                            />
                        </div>
                    )}
                </div>
            )}
            {isLeftPanelOpen && body}
        </div>
    );

    const contentMobile = screenSize.width <= 744 && (
        <div className={classes.root}>
            <div className={classes.navigationBars}>
                <PanelLeftExpand28Regular
                    className={classes.navigationIcon}
                    onClick={() => {
                        setIsOpen(true);
                    }}
                />
            </div>
            <Drawer
                type="overlay"
                position="end"
                modalType="non-modal"
                size="large"
                separator
                open={isOpen}
                onOpenChange={(_, { open }) => {
                    setIsOpen(open);
                }}
                className={classes.chatListDrawer}
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        className={classes.drawerHeaderTitle}
                        action={
                            <Button
                                appearance="transparent"
                                aria-label="Close"
                                icon={<DismissCircle32Regular color={customTokens.ekfbTeal} />}
                                onClick={() => {
                                    setIsOpen(false);
                                }}
                                className={classes.drawerCloseButton}
                                title="Close navigation"
                            />
                        }
                    >
                        {header}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody className={classes.drawerBody} style={{ paddingBottom: '0' }}>
                    {body}
                </DrawerBody>
            </Drawer>
        </div>
    );

    return (
        <div className={props.className ?? ''}>
            {contentDesktop}
            {contentMobile}
        </div>
    );
};
